import type { DueHabitView } from '@contracts';
import { CheckControl } from '@ui/shared/primitives/CheckControl';
import { habitCategoryMeta } from '@ui/shared/habits/habitCategory';
import { ShieldCheck, Plus } from '@ui/shared/icons';

/**
 * Habit rows for the merged Today view (phase-2b-habits.md SS2.2): reuses
 * phone-check-in.md's habit-row VISUAL design (category-tinted icon tile,
 * weekly-progress sub-line, done/skip or clean/log controls, no left-border
 * state rule) but adapted to today-view.md's ruled-list ROW GEOMETRY
 * (top-border-only rows, DueRow's shell) rather than phone-check-in.md's
 * original 60pt-card-per-row shape — "rows share the ruled-list geometry
 * today-view.md already ships; habits do not import a competing card style."
 */

type BuildHabitView = Extract<DueHabitView, { habitKind: 'build' }>;
type AvoidHabitView = Extract<DueHabitView, { habitKind: 'avoid' }>;

/** A build habit's due/done/skipped row inside the merged "Due today" ruled list. */
export function BuildHabitRow({
  habit,
  isLast,
  onToggleDone,
  onToggleSkip,
}: {
  readonly habit: BuildHabitView;
  readonly isLast: boolean;
  readonly onToggleDone: () => void;
  readonly onToggleSkip: () => void;
}) {
  const done = habit.status === 'done';
  const skipped = habit.status === 'skipped';
  const weekLabel =
    habit.weekProgress.target >= 7
      ? 'Daily'
      : `${habit.weekProgress.done}/${habit.weekProgress.target} this week`;

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <CategoryIconTile category={habit.category} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--type-body-size)',
            fontWeight: 'var(--type-body-weight-emphasis)',
            color: done || skipped ? 'var(--text-muted)' : 'var(--text-primary)',
          }}
        >
          {habit.name}
        </div>
        <div
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-muted)',
            marginTop: 'var(--space-1)',
          }}
        >
          {skipped ? 'Skipped today' : weekLabel}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 'none' }}>
        {/* Skip: only meaningful while pending — a done/skipped row already
            has its one active control (the toggle below) and re-showing a
            second control invites accidental double-taps on a row that's
            already resolved for the day. */}
        {!done && (
          <button
            type="button"
            onClick={onToggleSkip}
            aria-label={skipped ? `${habit.name}, skipped. Tap to undo.` : `Skip ${habit.name} today`}
            aria-pressed={skipped}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: skipped ? 'var(--text-secondary)' : 'var(--text-muted)',
              background: skipped ? 'var(--surface-hover)' : 'transparent',
              borderRadius: 'var(--radius-pill)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            </svg>
          </button>
        )}
        {!skipped && (
          <CheckControl
            checked={done}
            onToggle={onToggleDone}
            label={done ? `${habit.name}, completed. Tap to undo.` : `Mark ${habit.name} done`}
          />
        )}
      </div>
    </div>
  );
}

/** An avoid habit's row inside "Keeping in check" (phone-check-in.md SS1.5). */
export function AvoidHabitRow({
  habit,
  isLast,
  onLogClean,
  onLogLapse,
}: {
  readonly habit: AvoidHabitView;
  readonly isLast: boolean;
  readonly onLogClean: () => void;
  readonly onLogLapse: () => void;
}) {
  const clean = habit.status === 'clean';
  const overAllowance = habit.allowance.used > habit.allowance.limit;
  const subLabel =
    habit.allowance.limit === 0 && habit.allowance.used === 0
      ? 'Aiming for zero this week'
      : overAllowance
        ? `${habit.allowance.used} this week — allowance is ${habit.allowance.limit}`
        : `${habit.allowance.used} of ${habit.allowance.limit} used this week`;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'center',
          padding: 'var(--space-3) 0',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
        }}
      >
        <CategoryIconTile category={habit.category} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 'var(--type-body-weight-emphasis)',
              color: 'var(--text-primary)',
            }}
          >
            {habit.name}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-1)',
            }}
          >
            <AllowanceDots used={habit.allowance.used} limit={habit.allowance.limit} />
            <span
              className="num"
              style={{
                fontSize: 'var(--type-caption-size)',
                // Neutral tone regardless of over-allowance — state.neutralMiss,
                // never red, never bold (AC-B12 adversarial sweep).
                color: 'var(--text-secondary)',
              }}
            >
              {clean ? 'Clean today' : subLabel}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 'none' }}>
          <button
            type="button"
            onClick={onLogClean}
            aria-label={clean ? `${habit.name} logged clean today. Tap to undo.` : `Log ${habit.name} clean today`}
            aria-pressed={clean}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: clean ? 'var(--text-on-accent)' : 'var(--text-secondary)',
              background: clean
                ? `color-mix(in srgb, var(--accent-${habitCategoryMeta(habit.category).tokenSuffix}-vivid) 100%, transparent)`
                : 'transparent',
              borderRadius: 'var(--radius-pill)',
              border: clean ? 'none' : '1.5px solid var(--border-strong)',
            }}
          >
            <ShieldCheck size={16} aria-hidden="true" />
          </button>
          {/* Deliberately not a check/x pair (phone-check-in.md SS1.5):
              logging a lapse is entry, not failure — a plain "+" glyph. */}
          <button
            type="button"
            onClick={onLogLapse}
            aria-label={`Log one for ${habit.name}`}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: 'var(--text-secondary)',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)',
            }}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      {overAllowance && (
        <p
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-secondary)',
            margin: 'var(--space-1) 0 0',
          }}
        >
          The weekly review can help adjust this.
        </p>
      )}
    </div>
  );
}

function CategoryIconTile({ category }: { readonly category: DueHabitView['category'] }) {
  const meta = habitCategoryMeta(category);
  const Icon = meta.Icon;
  return (
    <span
      aria-hidden="true"
      style={{
        width: '32px',
        height: '32px',
        borderRadius: 'var(--radius-sm)',
        background: `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 16%, var(--surface-raised))`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <Icon size={16} color={`var(--accent-${meta.tokenSuffix})`} aria-hidden />
    </span>
  );
}

function AllowanceDots({ used, limit }: { readonly used: number; readonly limit: number }) {
  if (limit === 0) return null;
  const dots = Array.from({ length: limit }, (_, i) => i < used);
  return (
    <span style={{ display: 'flex', gap: '4px' }} aria-hidden="true">
      {dots.map((filled, i) => (
        <span
          key={i}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: 'var(--radius-pill)',
            background: filled ? 'var(--text-secondary)' : 'transparent',
            border: filled ? 'none' : '1px solid var(--border-strong)',
          }}
        />
      ))}
    </span>
  );
}
